import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { createSvgIcon, Grid, useMediaQuery, Typography, Chip, CircularProgress } from "@mui/material";
import { SchemesApi } from "apis/SchemesApi";
import { ProductsApi } from "apis/ProductsApi";
import { BrandsApi } from "apis/BrandsApi";
import TableHeader from "components/Table/TableHeader";
import { SchemeRow, AddSchemeModal } from "components/SchemesV2";
import CircularIndeterminateModal from "../../utils/Loading";
import { setLoadingPanel } from "../../redux/features/loading";
import { getDefaultDates } from "utils";

const PlusIcon = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>,
  "Plus"
);

const showToast = (message, type = "error") => {
  toast(message, { type, position: toast.POSITION.TOP_CENTER, autoClose: 1000 });
};

const handleApiError = (err, navigate) => {
  if (err?.response?.data) {
    showToast(`${err.response.status} ${err.response.data.message}`);
  }
  if (err?.response?.status === 401) {
    localStorage.clear();
    navigate("/");
  }
};

export default function SchemesV2() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:768px)");
  const isLoading = useSelector((state) => state.loadingReducer.isLoading);
  const debounceTimerRef = useRef(null);
  const defaultDates = getDefaultDates();

  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [keyword, setKeyword] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [showDrafts, setShowDrafts] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [datePayload, setDatePayload] = useState(defaultDates);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [bulkFolder, setBulkFolder] = useState("");
  const [bulkFiles, setBulkFiles] = useState([]);
  const [isUploadingBulk, setIsUploadingBulk] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [s3Folders, setS3Folders] = useState([]);
  const [isFetchingFolders, setIsFetchingFolders] = useState(false);
  const bulkFileRef = useRef(null);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (selectedBrand?.id) {
      fetchSchemes(selectedBrand.id, 0);
    }
  }, [datePayload, selectedProduct, isActiveFilter, showDrafts]);

  const fetchBrands = () => {
    dispatch(setLoadingPanel(true));
    BrandsApi.GetBrandsListByFilter({ page: 0, size: 20 })
      .then((res) => {
        const activeBrands = res.data.data.filter((d) => d.isActive);
        setBrands(activeBrands);
        if (activeBrands.length > 0) {
          setSelectedBrand(activeBrands[0]);
          fetchProducts(activeBrands[0].id);
          fetchSchemes(activeBrands[0].id, 0);
        }
        dispatch(setLoadingPanel(false));
      })
      .catch((err) => {
        dispatch(setLoadingPanel(false));
        handleApiError(err, navigate);
      });
  };

  const fetchSchemes = (brandId, page) => {
    if (!brandId) return;

    // If drafts filter is enabled, fetch drafts instead
    if (showDrafts) {
      SchemesApi.GetSchemeExtractDrafts()
        .then((res) => {
          setSchemes(res.data.data || []);
          setTotalElements(res.data.totalElements || 0);
          setTotalPages(res.data.pages || 0);
        })
        .catch((err) => handleApiError(err, navigate));
      return;
    }

    SchemesApi.GetByV2Filter({
      page,
      size: 20,
      brandId,
      productId: selectedProduct?.id || undefined,
      keyword: keyword || undefined,
      isActive: isActiveFilter !== "" ? isActiveFilter : undefined,
      periodFrom: datePayload.periodFrom,
      periodTo: datePayload.periodTo,
    })
      .then((res) => {
        setSchemes(res.data.data);
        setTotalElements(res.data.totalElements);
        setTotalPages(res.data.pages);
      })
      .catch((err) => handleApiError(err, navigate));
  };

  const fetchProducts = (brandId) => {
    if (!brandId) return;

    ProductsApi.GetProductsListByFilter({ page: 0, size: 100, brandId, isActive: true })
      .then((res) => setProducts(res.data.data || []))
      .catch(() => setProducts([]));
  };

  const handleBrandChange = (event) => {
    const brand = event.target.value;
    setSelectedBrand(brand);
    setSelectedProduct("");
    setProducts([]);
    fetchProducts(brand.id);
    fetchSchemes(brand.id, 0);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setKeyword(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      SchemesApi.GetByV2Filter({
        page: 0,
        size: 20,
        brandId: selectedBrand.id,
        productId: selectedProduct?.id || undefined,
        keyword: value || undefined,
        isActive: isActiveFilter !== "" ? isActiveFilter : undefined,
        periodFrom: datePayload.periodFrom,
        periodTo: datePayload.periodTo,
      })
        .then((res) => {
          setSchemes(res.data.data);
          setTotalElements(res.data.totalElements);
          setTotalPages(res.data.pages);
        })
        .catch((err) => handleApiError(err, navigate));
    }, 500);
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
    fetchSchemes(selectedBrand.id, page - 1);
  };

  const handleFetchFolders = () => {
    setIsFetchingFolders(true);
    SchemesApi.GetSchemeFolders()
      .then((res) => setS3Folders(res.data?.data || []))
      .catch((err) => showToast(err?.response?.data?.message || "Error fetching folders"))
      .finally(() => setIsFetchingFolders(false));
  };

  const handleBulkUpload = () => {
    if (!bulkFolder.trim() || bulkFiles.length === 0) return;
    const formData = new FormData();
    bulkFiles.forEach((file) => formData.append("files", file));
    setIsUploadingBulk(true);
    SchemesApi.BulkUploadSchemesV2(bulkFolder.trim(), formData)
      .then((res) => {
        setUploadedFiles(res.data?.data || []);
        setBulkFiles([]);
        if (bulkFileRef.current) bulkFileRef.current.value = "";
        showToast(`${res.data?.data?.length || 0} file(s) uploaded successfully`, "success");
        handleFetchFolders();
      })
      .catch((err) => showToast(err?.response?.data?.message || "Error uploading files"))
      .finally(() => setIsUploadingBulk(false));
  };

  const handleExtractFolder = (folder) => {
    SchemesApi.ExtractSchemeFolder(folder).catch(() => {});
    showToast("Extraction started in background. Drafts will appear in the list shortly.", "success");
  };

  const handleReset = () => {
    setDatePayload(defaultDates);
    setSelectedProduct("");
    setKeyword("");
    setIsActiveFilter("");
    setShowDrafts(false);
    setCurrentPage(1);
    fetchSchemes(selectedBrand.id, 0);
  };

  return (
    <>
      <CircularIndeterminateModal open={isLoading && !brands.length} />

      <div className="relative flex flex-col items-center md:pt-8 md:px-8 min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
        <div className="flex xl:flex-row flex-col gap-3 md:gap-4 w-full">
          <div className="w-full xl:w-[80%] p-4 md:px-8 md:py-4">
            <Box className="w-full">
              <FormControl fullWidth>
                <InputLabel id="brand-select-label">Select Brand</InputLabel>
                <Select
                  labelId="brand-select-label"
                  value={selectedBrand}
                  label="Select Brand"
                  onChange={handleBrandChange}
                  sx={{
                    height: "40px",
                    backgroundColor: "#F8FAFC",
                    borderRadius: "0.5rem",
                    "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#E2E8F0" } },
                    "& .MuiOutlinedInput-root:hover": { "& fieldset": { borderColor: "#CBD5E1" } },
                    "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { borderColor: "#4F46E5" } }
                  }}
                >
                  {brands.map((b) => (
                    <MenuItem key={b.id} value={b}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </div>
          <div className="w-full xl:w-[30%] md:px-8 md:py-4 p-4 flex items-center">
            <Button
              onClick={() => { setEditingScheme(null); setOpen(true); }}
              fullWidth
              variant="contained"
              color="primary"
              sx={{ height: "40px" }}
              startIcon={<PlusIcon />}
            >
              Add Scheme
            </Button>
          </div>
        </div>

        <div className="w-full md:px-8 md:py-2 p-4">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth sx={{ "& .MuiInputLabel-root": { top: "-8px" } }}>
                <InputLabel id="product-select-label">Select Product</InputLabel>
                <Select
                  labelId="product-select-label"
                  value={selectedProduct}
                  label="Select Product"
                  onChange={(e) => {
                    setSelectedProduct(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={{
                    height: "40px",
                    backgroundColor: "#F8FAFC",
                    borderRadius: "0.5rem",
                    "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#E2E8F0" } },
                    "& .MuiOutlinedInput-root:hover": { "& fieldset": { borderColor: "#CBD5E1" } },
                    "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { borderColor: "#4F46E5" } }
                  }}
                >
                  <MenuItem value="">All Products</MenuItem>
                  {products.map((p) => (
                    <MenuItem key={p.id} value={p}>{p.primaryName} {p.secondaryName || ""}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Search by Keyword"
                value={keyword}
                onChange={handleSearch}
                placeholder="Enter keyword..."
                sx={{
                  "& .MuiInputBase-root": { height: "40px", backgroundColor: "#F8FAFC", borderRadius: "0.5rem" },
                  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#E2E8F0" } },
                  "& .MuiOutlinedInput-root:hover": { "& fieldset": { borderColor: "#CBD5E1" } },
                  "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { borderColor: "#4F46E5" } }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth sx={{ "& .MuiInputLabel-root": { top: "-8px" } }}>
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  value={isActiveFilter}
                  label="Status"
                  onChange={(e) => {
                    setIsActiveFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={{
                    height: "40px",
                    backgroundColor: "#F8FAFC",
                    borderRadius: "0.5rem",
                    "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#E2E8F0" } },
                    "& .MuiOutlinedInput-root:hover": { "& fieldset": { borderColor: "#CBD5E1" } },
                    "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { borderColor: "#4F46E5" } }
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth sx={{ "& .MuiInputLabel-root": { top: "-8px" } }}>
                <InputLabel id="drafts-select-label">View</InputLabel>
                <Select
                  labelId="drafts-select-label"
                  value={showDrafts}
                  label="View"
                  onChange={(e) => {
                    setShowDrafts(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={{
                    height: "40px",
                    backgroundColor: "#F8FAFC",
                    borderRadius: "0.5rem",
                    "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#E2E8F0" } },
                    "& .MuiOutlinedInput-root:hover": { "& fieldset": { borderColor: "#CBD5E1" } },
                    "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { borderColor: "#4F46E5" } }
                  }}
                >
                  <MenuItem value={false}>Published Schemes</MenuItem>
                  <MenuItem value={true}>Drafts</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Period From"
                value={datePayload.periodFrom}
                onChange={(e) => setDatePayload((prev) => ({ ...prev, periodFrom: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiInputBase-root": { height: "40px", backgroundColor: "#F8FAFC", borderRadius: "0.5rem" },
                  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#E2E8F0" } },
                  "& .MuiOutlinedInput-root:hover": { "& fieldset": { borderColor: "#CBD5E1" } },
                  "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { borderColor: "#4F46E5" } }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Period To"
                value={datePayload.periodTo}
                onChange={(e) => setDatePayload((prev) => ({ ...prev, periodTo: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiInputBase-root": { height: "40px", backgroundColor: "#F8FAFC", borderRadius: "0.5rem" },
                  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#E2E8F0" } },
                  "& .MuiOutlinedInput-root:hover": { "& fieldset": { borderColor: "#CBD5E1" } },
                  "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { borderColor: "#4F46E5" } }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleReset}
                sx={{ height: { xs: "40px", md: "52px" } }}
              >
                Reset All
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>

      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
        <div className="flex items-center justify-between p-4 md:px-8 md:py-4">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Bulk S3 Upload &amp; AI Extraction</Typography>
          <Button
            variant={showBulkPanel ? "outlined" : "contained"}
            color="info"
            size="small"
            onClick={() => {
              if (!showBulkPanel) handleFetchFolders();
              setShowBulkPanel((v) => !v);
            }}
          >
            {showBulkPanel ? "Hide" : "Open"}
          </Button>
        </div>

        {showBulkPanel && (
          <div className="px-4 md:px-8 pb-6">

            {/* Step 1: Upload */}
            <Box sx={{ mb: 3, p: 2, border: "1px dashed #cbd5e1", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 2 }}>
                Step 1 — Upload Files to S3
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Folder Name"
                    placeholder="e.g. samsung-may-2026"
                    value={bulkFolder}
                    onChange={(e) => setBulkFolder(e.target.value)}
                    sx={{ "& .MuiInputBase-root": { height: "40px", backgroundColor: "#F8FAFC" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ height: "40px" }}
                    onClick={() => bulkFileRef.current?.click()}
                  >
                    {bulkFiles.length > 0 ? `${bulkFiles.length} file(s) selected` : "Choose PDFs / Images"}
                  </Button>
                  <input
                    ref={bulkFileRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    style={{ display: "none" }}
                    onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ height: "40px" }}
                    disabled={isUploadingBulk || !bulkFolder.trim() || bulkFiles.length === 0}
                    onClick={handleBulkUpload}
                  >
                    {isUploadingBulk ? <CircularProgress size={18} color="inherit" /> : "Upload to S3"}
                  </Button>
                </Grid>
              </Grid>

              {uploadedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                    Uploaded {uploadedFiles.length} file(s):
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {uploadedFiles.map((f) => (
                      <Chip
                        key={f.key}
                        label={f.name}
                        size="small"
                        color="success"
                        variant="outlined"
                        component="a"
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        clickable
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Step 2 + 3: Folders & Extract */}
            <Box sx={{ p: 2, border: "1px dashed #cbd5e1", borderRadius: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Step 2 — S3 Folders &amp; AI Extraction
                </Typography>
                <Button size="small" variant="outlined" onClick={handleFetchFolders} disabled={isFetchingFolders}>
                  {isFetchingFolders ? <CircularProgress size={16} /> : "Refresh"}
                </Button>
              </Box>

              {!isFetchingFolders && s3Folders.length === 0 && (
                <Typography variant="body2" color="text.secondary">No folders found. Upload files first.</Typography>
              )}

              {s3Folders.map((folder) => (
                <Box key={folder.folder} sx={{ mb: 2, p: 2, backgroundColor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{folder.folder}</Typography>
                      <Chip label={`${folder.files.length} files`} size="small" />
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={() => handleExtractFolder(folder.folder)}
                    >
                      Extract with AI
                    </Button>
                  </Box>

                  <Box sx={{ pl: 1 }}>
                    {folder.files.map((file) => (
                      <Box key={file.key} sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.25 }}>
                        <Typography variant="caption" sx={{ flexGrow: 1 }}>{file.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{(file.size / 1024).toFixed(0)} KB</Typography>
                        <Typography variant="caption" color="text.secondary">{new Date(file.lastModified).toLocaleDateString()}</Typography>
                        <a href={file.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#4F46E5" }}>View</a>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </div>
        )}
      </div>

      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
        <div className="block w-full overflow-x-auto py-3 px-4 lg:p-8">
          <Stack
            spacing={2}
            direction={{ md: "row", xs: "column" }}
            justifyContent={{ md: "space-between", xs: "center" }}
            className="w-full"
          >
            <div className="flex justify-center md:justify-end py-2 md:py-4 px-2 text-sky-500">
              Total: {totalElements}
            </div>
            <div className="mb-2 flex justify-center">
              <Pagination
                count={totalPages}
                page={currentPage}
                color="primary"
                onChange={handlePageChange}
                size={isMobile ? "small" : "medium"}
                className="mb-4"
              />
            </div>
          </Stack>

          <table className="w-full border-collapse">
            <thead>
              <tr>
                <TableHeader heading="#" />
                <TableHeader heading="Name" />
                <TableHeader heading="Image" />
                <TableHeader heading="Start Date" />
                <TableHeader heading="End Date" />
                <TableHeader heading="Actions" />
                <TableHeader heading="Scheme Status" />
                <TableHeader heading="Active" />
              </tr>
            </thead>
            <tbody>
              {schemes.map((scheme, index) => (
                <SchemeRow key={scheme.id || index} scheme={scheme} index={index} onEdit={(s) => { setEditingScheme(s); setOpen(true); }} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AddSchemeModal
        open={open}
        setOpen={setOpen}
        brands={brands}
        periodFrom={defaultDates.periodFrom}
        periodTo={defaultDates.periodTo}
        editingScheme={editingScheme}
        onSaved={() => fetchSchemes(selectedBrand.id, currentPage - 1)}
      />

      <ToastContainer />
    </>
  );
}
